import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
// React
import React, { useState, useEffect, useRef, useMemo } from 'react';

// MUI
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Backdrop, CircularProgress, Grid, Dialog, Box, Typography, IconButton, Snackbar, Alert, CssBaseline, DialogTitle, DialogActions, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Libs

import i18n from './i18n';

// Components
import RegisterTab from './components/registerTab';
import NavigationButtons from './components/navigationButtons';
import RegisterUserDetails from './components/registerUserDetails';
import RegisterCompanyDetails from './components/registerCompanyDetails';
import RegisterDocumentDetails from './components/registerDocumentDetails';
import SuccessPage from './components/successPage';
import CustomOnboardingForm from './components/customOnboarding';

// Constants
import { regions, roles, displayRoles } from './configurations/settings';
import { dynamicForm } from './configurations/onboardingForm';

//Interfaces

// API
import { getStoreId } from './api/getStoreId';
import { retrieveUserCompanyStatus } from './api/retrieveUserCompanyStatus';
import { retrieveUserCompanyDetails } from './api/retrieveUserCompanyDetails';
import { getCountryList } from './api/getCountryList';
import { getRegionSpecificCountryList } from './api/getRegionSpecificCountryList';
import { getUserCompanyAttributes } from './api/getUserAttributes';
import { getBusinessTypes } from './api/getBusinessTypes';

// Utils
import { generatePayloadObject, getThemeOptions, isEmpty, getAxiosInstance } from './utils/util';

// Context
import { UserDetailsContext, userDetails } from './context/userContext';
import { CompanyDetailsContext, companyDetails } from './context/companyContext';
import { CountryListDetailsContext, countryListDetails } from './context/countryContext';
import { UserCompanyAttributeDetailsContext, userCompAttributeDetails } from './context/compAttributes';
import { BusinessTypeDetailsContext, businessTypeDetails } from './context/businessTypesContext';
import { ConsentDetailsContextV1, consentDetailsV1 } from './context/consentContextV1';
import { ConsentDetailsContextV2, consentDetailsV2 } from './context/consentContextV2';
import DeviceBreakPoints from './configurations/deviceBreakpoints';
import { PanFullNameContext, panNameSection } from './context/panFullNameContext';

/**
 * Renders company onboarding form with all the information to be filled by the user such as user, company details, documents etc.
 * 
 * @param {IRegisterCompany} props Input paramteres for executing onboarding flow.
 * @returns {JSX.Element} React Element which renders onboarding form.
 */
var RegisterCompany = function RegisterCompany(props) {
  var _userData$user6, _props$dynamicForm2, _props$configOptions, _props$configOptions2, _props$configOptions3, _props$configOptions4, _props$configOptions5, _props$configOptions6, _props$configOptions7, _props$configOptions8, _props$configOptions9, _props$configOptions10;
  var theme = createTheme(getThemeOptions(props.fontName, props === null || props === void 0 ? void 0 : props.configOptions));
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    isValidInput = _useState2[0],
    setValidInput = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isSubmitDisabled = _useState4[0],
    setIsSubmitDisabled = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    openConfirmModal = _useState6[0],
    setOpenConfirmModal = _useState6[1];
  var _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    dcsJWTToken = _useState8[0],
    setDCSJWTToken = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    loader = _useState10[0],
    _setLoader = _useState10[1];
  var _useState11 = useState(''),
    _useState12 = _slicedToArray(_useState11, 2),
    requestorPayload = _useState12[0],
    setRequestorPayload = _useState12[1];
  var _useState13 = useState({
      storeId: '',
      tenantId: ''
    }),
    _useState14 = _slicedToArray(_useState13, 2),
    store = _useState14[0],
    setStore = _useState14[1];
  var _useState15 = useState(userDetails),
    _useState16 = _slicedToArray(_useState15, 2),
    userData = _useState16[0],
    setUserData = _useState16[1];
  var _useState17 = useState(companyDetails),
    _useState18 = _slicedToArray(_useState17, 2),
    companyData = _useState18[0],
    setCompanyData = _useState18[1];
  var _useState19 = useState(panNameSection),
    _useState20 = _slicedToArray(_useState19, 2),
    panNameValue = _useState20[0],
    setPanNameValue = _useState20[1];
  var _useState21 = useState(countryListDetails.countryList),
    _useState22 = _slicedToArray(_useState21, 2),
    countryData = _useState22[0],
    setCountryData = _useState22[1];
  var _useState23 = useState(userCompAttributeDetails.data),
    _useState24 = _slicedToArray(_useState23, 2),
    userCompAttributesData = _useState24[0],
    setUserCompAttributes = _useState24[1];
  var _useState25 = useState(businessTypeDetails),
    _useState26 = _slicedToArray(_useState25, 2),
    businessTypeData = _useState26[0],
    setBusinessTypes = _useState26[1];
  var _useState27 = useState(false),
    _useState28 = _slicedToArray(_useState27, 2),
    openRegisterModal = _useState28[0],
    setOpenRegisterModal = _useState28[1];
  var _useState29 = useState(1),
    _useState30 = _slicedToArray(_useState29, 2),
    activeTab = _useState30[0],
    setActiveTab = _useState30[1];
  var _useState31 = useState(false),
    _useState32 = _slicedToArray(_useState31, 2),
    snackbar = _useState32[0],
    _setSnackbar = _useState32[1];
  var _useState33 = useState(i18n.t('REGISTRATION.DETAILS_SAVED')),
    _useState34 = _slicedToArray(_useState33, 2),
    snackBarMessage = _useState34[0],
    _setSnackBarMessage = _useState34[1];
  var _useState35 = useState('success'),
    _useState36 = _slicedToArray(_useState35, 2),
    snackBarSeverity = _useState36[0],
    _setSnackBarSeverity = _useState36[1];
  var _useState37 = useState(dynamicForm),
    _useState38 = _slicedToArray(_useState37, 2),
    dynamicFormDisplay = _useState38[0],
    setDynamicFormDisplay = _useState38[1];
  var _useState39 = useState(false),
    _useState40 = _slicedToArray(_useState39, 2),
    initialCompanyDetailsCall = _useState40[0],
    setInitialCompanyDetailsCall = _useState40[1];
  var _useState41 = useState('16px'),
    _useState42 = _slicedToArray(_useState41, 2),
    paddingOnScroll = _useState42[0],
    setPaddingOnScroll = _useState42[1];
  var _useState43 = useState(consentDetailsV1.consentList),
    _useState44 = _slicedToArray(_useState43, 2),
    consentDataV1 = _useState44[0],
    setConsentDataV1 = _useState44[1];
  var _useState45 = useState(consentDetailsV2.consentList),
    _useState46 = _slicedToArray(_useState45, 2),
    consentDataV2 = _useState46[0],
    setConsentDataV2 = _useState46[1];
  var _useState47 = useState(''),
    _useState48 = _slicedToArray(_useState47, 2),
    tempCountryCode = _useState48[0],
    setTempCountryCode = _useState48[1];
  var _DeviceBreakPoints = DeviceBreakPoints(),
    isSmallDevice = _DeviceBreakPoints.isSmallDevice,
    isMediumDevice = _DeviceBreakPoints.isMediumDevice;
  var modalRef = useRef(null);
  var outerModalRef = useRef(null);
  var childRef = useRef(null);
  var axiosInstance = getAxiosInstance(props === null || props === void 0 ? void 0 : props.environment);
  var genericPayload = generatePayloadObject(axiosInstance, props === null || props === void 0 ? void 0 : props.clientToken, store.tenantId, props === null || props === void 0 ? void 0 : props.role, props.buyerAuthPayload, props.dcsJWTToken);
  var tenantId = (props === null || props === void 0 ? void 0 : props.role) === roles.BUYER_ROLE ? store.tenantId : '';
  useEffect(function () {
    if (modalRef.current && outerModalRef.current) {
      setPaddingOnScroll(17 - (outerModalRef.current.offsetWidth - outerModalRef.current.scrollWidth) + 'px');
      if (modalRef.current.scrollTop > 0) {
        modalRef.current.scrollTop = 0;
      }
    }
  }, [activeTab]);
  var handleSnackbar = function handleSnackbar(event, reason) {
    if (reason === 'clickaway') {
      return;
    }
    _setSnackbar(false);
  };
  var handleActiveTab = function handleActiveTab(value) {
    setActiveTab(value);
  };
  var handleButtonClick = function handleButtonClick(event) {
    var input = event.target;
    if (childRef.current && input.textContent) childRef.current.handleSaveAndNext(input.textContent);
  };
  var handleSubmit = function handleSubmit() {
    if (childRef.current) childRef.current.handleSubmitForApproval();
    setOpenConfirmModal(false);
  };
  var setUserDetailsFromInput = function setUserDetailsFromInput(userDataFromInput) {
    setUserData(userDataFromInput);
  };
  var setCompanyDetailsFromInput = function setCompanyDetailsFromInput(companyDataFromInput) {
    setCompanyData(companyDataFromInput);
  };
  var setPanNameFromInput = function setPanNameFromInput(panNameInput) {
    setPanNameValue(panNameInput);
  };
  var setConsentDetailsV1FromInput = function setConsentDetailsV1FromInput(consentData) {
    setConsentDataV1(consentData);
  };
  var setConsentDetailsV2FromInput = function setConsentDetailsV2FromInput(consentData) {
    setConsentDataV2(consentData);
  };
  var validateInput = function validateInput() {
    var isValid = true;
    if (!props.clientToken || typeof props.errorHandler !== 'function' || typeof props.successHandler !== 'function' || props.errorHandler.length !== 1 || props.successHandler.length !== 1 || props.language && props.language !== 'de' && props.language !== 'en' || props.role && props.role !== roles.SELLER_ROLE && props.role !== roles.BUYER_ROLE || props.role === roles.SELLER_ROLE && !props.regionCode || props.regionCode && (props === null || props === void 0 ? void 0 : props.role) !== roles.SELLER_ROLE || props.regionCode && props.regionCode !== regions.apacRegionCode && props.regionCode !== regions.euRegionCode && props.regionCode !== regions.naRegionCode) {
      isValid = false;
      setValidInput(false);
      return isValid;
    }
    if (props.dcsJWTToken) {
      setDCSJWTToken(props.dcsJWTToken);
    }
    if (props.buyerAuthPayload) {
      setRequestorPayload(props.buyerAuthPayload);
    }
    setValidInput(true);
    return isValid;
  };
  var handleAPIFailure = function handleAPIFailure(errorResponse) {
    var _errorResponse$respon, _errorResponse$respon2;
    var error = errorResponse === null || errorResponse === void 0 || (_errorResponse$respon = errorResponse.response) === null || _errorResponse$respon === void 0 ? void 0 : _errorResponse$respon.data;
    _setLoader(false);
    props.errorHandler({
      message: error ? error === null || error === void 0 ? void 0 : error.message : errorResponse === null || errorResponse === void 0 ? void 0 : errorResponse.message,
      statusCode: errorResponse !== null && errorResponse !== void 0 && errorResponse.response ? errorResponse === null || errorResponse === void 0 || (_errorResponse$respon2 = errorResponse.response) === null || _errorResponse$respon2 === void 0 ? void 0 : _errorResponse$respon2.status : undefined,
      error: errorResponse !== null && errorResponse !== void 0 && errorResponse.code ? errorResponse === null || errorResponse === void 0 ? void 0 : errorResponse.code : undefined
    });
  };
  var getStoreDetails = function getStoreDetails() {
    _setLoader(true);
    getStoreId(genericPayload).then(function (response) {
      if (response.data) {
        _setLoader(false);
        setStore(response.data);
      } else {
        _setLoader(false);
        props.errorHandler({
          message: i18n.t('ERROR_NO_STORE_ID')
        });
      }
    }, function (error) {
      handleAPIFailure(error);
    });
  };
  var handleCountryResponse = function handleCountryResponse(response) {
    if (response.data) {
      _setLoader(false);
      setCountryData(_toConsumableArray(response.data).sort(function (country1, country2) {
        return country1.countryName.localeCompare(country2.countryName);
      }));
    } else {
      _setLoader(false);
      props.errorHandler({
        message: i18n.t('ERROR_NO_COUNTRY_DATA')
      });
    }
  };
  var getCountryListDetails = function getCountryListDetails() {
    getCountryList(genericPayload).then(function (response) {
      handleCountryResponse(response);
    }, function (error) {
      handleAPIFailure(error);
    });
  };
  var getRegionSpecificCountryDetails = function getRegionSpecificCountryDetails() {
    getRegionSpecificCountryList(genericPayload, props === null || props === void 0 ? void 0 : props.regionCode).then(function (response) {
      handleCountryResponse(response);
    }, function (error) {
      handleAPIFailure(error);
    });
  };
  var getUserCompanyAttributesFunc = function getUserCompanyAttributesFunc() {
    var _userData$user, _userData$user2;
    _setLoader(true);
    getUserCompanyAttributes(userData !== null && userData !== void 0 && (_userData$user = userData.user) !== null && _userData$user !== void 0 && _userData$user.countryCode ? userData === null || userData === void 0 || (_userData$user2 = userData.user) === null || _userData$user2 === void 0 ? void 0 : _userData$user2.countryCode : 'IN', genericPayload).then(function (response) {
      if (response.data) {
        _setLoader(false);
        setUserCompAttributes(response.data);
      } else {
        _setLoader(false);
        props.errorHandler({
          message: i18n.t('ERROR_NO_ATTRIBUTES')
        });
      }
    }, function (error) {
      handleAPIFailure(error);
    });
  };
  var getBusinessTypesFunc = function getBusinessTypesFunc() {
    _setLoader(true);
    getBusinessTypes(genericPayload).then(function (response) {
      if (response.data) {
        _setLoader(false);
        setBusinessTypes(response.data);
      } else {
        _setLoader(false);
        props.errorHandler({
          message: i18n.t('ERROR_NO_BUSINESSTYPE_DATA ')
        });
      }
    }, function (error) {
      handleAPIFailure(error);
    });
  };
  useEffect(function () {
    i18n.init({
      lng: props.language ? props.language : 'en'
    });
    if (!validateInput()) {
      props.errorHandler({
        message: i18n.t('ERROR_TEXT')
      });
    }
  }, []);
  useEffect(function () {
    if (isValidInput && props !== null && props !== void 0 && props.role && isEmpty(props === null || props === void 0 ? void 0 : props.dynamicForm)) {
      getStoreDetails();
    }
  }, [isValidInput]);
  useEffect(function () {
    var _props$dynamicForm;
    if (props !== null && props !== void 0 && (_props$dynamicForm = props.dynamicForm) !== null && _props$dynamicForm !== void 0 && (_props$dynamicForm = _props$dynamicForm.steps) !== null && _props$dynamicForm !== void 0 && _props$dynamicForm.length) {
      setDynamicFormDisplay(props === null || props === void 0 ? void 0 : props.dynamicForm);
      setOpenRegisterModal(true);
    }
  }, []);
  useEffect(function () {
    if (store.tenantId) {
      getBusinessTypesFunc();
      _setLoader(true);
      if (props !== null && props !== void 0 && props.regionCode) {
        getRegionSpecificCountryDetails();
      } else {
        getCountryListDetails();
      }
    }
  }, [store]);
  useEffect(function () {
    var _userData$user3, _userData$user4;
    if (userData !== null && userData !== void 0 && (_userData$user3 = userData.user) !== null && _userData$user3 !== void 0 && _userData$user3.countryCode && (userData === null || userData === void 0 || (_userData$user4 = userData.user) === null || _userData$user4 === void 0 ? void 0 : _userData$user4.countryCode) !== tempCountryCode) {
      var _userData$user5;
      getUserCompanyAttributesFunc();
      setTempCountryCode(userData === null || userData === void 0 || (_userData$user5 = userData.user) === null || _userData$user5 === void 0 ? void 0 : _userData$user5.countryCode);
    }
  }, [userData === null || userData === void 0 || (_userData$user6 = userData.user) === null || _userData$user6 === void 0 ? void 0 : _userData$user6.countryCode]);
  useEffect(function () {
    if (store.tenantId && countryData !== null && countryData !== void 0 && countryData.some(function (item) {
      return item.countryId !== '';
    })) {
      _setLoader(true);
      retrieveUserCompanyStatus(genericPayload).then(function (response) {
        if (response.data) {
          _setLoader(false);
          setUserData(response.data);
        } else {
          _setLoader(false);
          props.errorHandler({
            message: i18n.t('ERROR_NO_USER_DATA')
          });
        }
      }, function (error) {
        handleAPIFailure(error);
      });
    }
  }, [store, countryData]);
  useEffect(function () {
    if (userData.userId) {
      if (userData.userId && (!userData.areCompanyDetailsCaptured || !userData.dcsCompanyId) || !isEmpty(props === null || props === void 0 ? void 0 : props.dynamicForm)) {
        setOpenRegisterModal(true);
      } else {
        setOpenRegisterModal(false);
      }
      props === null || props === void 0 || props.successHandler({
        companyRegStatusDescription: userData === null || userData === void 0 ? void 0 : userData.companyRegStatusDescription,
        companyId: userData === null || userData === void 0 ? void 0 : userData.dcsCompanyId,
        status: userData === null || userData === void 0 ? void 0 : userData.companyRegisteredStatus,
        userId: userData === null || userData === void 0 ? void 0 : userData.userId
      });
    }
  }, [userData]);
  useEffect(function () {
    if (userData.companyId && !initialCompanyDetailsCall) {
      setInitialCompanyDetailsCall(true);
      _setLoader(false);
      retrieveUserCompanyDetails(userData.companyId, genericPayload).then(function (response) {
        if (response.data) {
          _setLoader(false);
          setCompanyData(response.data);
        } else {
          _setLoader(false);
          props.errorHandler({
            message: i18n.t('ERROR_NO_COMPANY_DATA')
          });
        }
      }, function (error) {
        handleAPIFailure(error);
      });
    }
  }, [userData]);
  var action = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(IconButton, {
    size: "small",
    "aria-label": "close",
    color: "inherit",
    onClick: handleSnackbar
  }, /*#__PURE__*/React.createElement(CloseIcon, {
    fontSize: "small"
  })));
  var getDisplayRole = function getDisplayRole() {
    if (!(props !== null && props !== void 0 && props.displayRole)) {
      if ((props === null || props === void 0 ? void 0 : props.role) === roles.BUYER_ROLE) {
        return displayRoles.CUSTOMER;
      } else if ((props === null || props === void 0 ? void 0 : props.role) === roles.SELLER_ROLE) {
        return displayRoles.PARTNER;
      }
    } else {
      return props === null || props === void 0 ? void 0 : props.displayRole;
    }
  };
  var getBooleanValue = function getBooleanValue(value) {
    setIsSubmitDisabled(value);
  };
  var getHeight = function getHeight() {
    if (activeTab === 4) {
      return '85%';
    } else if (isSmallDevice) {
      return '70%';
    }
    return '27rem';
  };
  var getComponent = function getComponent(order) {
    var _dynamicFormDisplay$s, _componentObject$, _userData$user7, _consentObj, _componentObject$2, _userData$user8;
    var componentObject = dynamicFormDisplay === null || dynamicFormDisplay === void 0 || (_dynamicFormDisplay$s = dynamicFormDisplay.steps) === null || _dynamicFormDisplay$s === void 0 ? void 0 : _dynamicFormDisplay$s.filter(function (item) {
      return item.order === order;
    });
    var consentObj = [];
    if ((_componentObject$ = componentObject[0]) !== null && _componentObject$ !== void 0 && _componentObject$.consentSection) consentObj = componentObject[0].consentSection;
    var environment = props !== null && props !== void 0 && props.environment ? props === null || props === void 0 ? void 0 : props.environment : 'development';
    if (isEmpty(props === null || props === void 0 ? void 0 : props.dynamicForm)) {
      switch (order) {
        case 1:
          return /*#__PURE__*/React.createElement(RegisterUserDetails, {
            ref: childRef,
            displayFields: componentObject[0].displaySections,
            role: props === null || props === void 0 ? void 0 : props.role,
            environment: environment,
            axiosInstance: axiosInstance,
            tenantId: tenantId,
            requestorPayload: requestorPayload,
            clientToken: props === null || props === void 0 ? void 0 : props.clientToken,
            dcsJwtToken: dcsJWTToken,
            regionCode: props === null || props === void 0 ? void 0 : props.regionCode,
            handleActiveTab: handleActiveTab,
            setLoader: function setLoader(value) {
              return _setLoader(value);
            },
            setSnackbar: function setSnackbar(value) {
              return _setSnackbar(value);
            },
            setSnackBarMessage: function setSnackBarMessage(message) {
              return _setSnackBarMessage(message);
            },
            setSnackBarSeverity: function setSnackBarSeverity(severity) {
              return _setSnackBarSeverity(severity);
            },
            setOpenRegisterModal: setOpenRegisterModal,
            cancelHandler: props === null || props === void 0 ? void 0 : props.cancelHandler,
            errorHandler: props === null || props === void 0 ? void 0 : props.errorHandler
          });
        case 2:
          return /*#__PURE__*/React.createElement(RegisterCompanyDetails, {
            ref: childRef,
            displayFields: componentObject[0].displaySections,
            role: props === null || props === void 0 ? void 0 : props.role,
            tenantId: tenantId,
            axiosInstance: axiosInstance,
            environment: environment,
            dcsJwtToken: dcsJWTToken,
            clientToken: props === null || props === void 0 ? void 0 : props.clientToken,
            requestorPayload: requestorPayload,
            handleActiveTab: handleActiveTab,
            setLoader: function setLoader(value) {
              return _setLoader(value);
            },
            setSnackbar: function setSnackbar(value) {
              return _setSnackbar(value);
            },
            countryCode: userData === null || userData === void 0 || (_userData$user7 = userData.user) === null || _userData$user7 === void 0 ? void 0 : _userData$user7.countryCode,
            setSnackBarMessage: function setSnackBarMessage(message) {
              return _setSnackBarMessage(message);
            },
            setSnackBarSeverity: function setSnackBarSeverity(severity) {
              return _setSnackBarSeverity(severity);
            },
            setOpenRegisterModal: setOpenRegisterModal,
            cancelHandler: props === null || props === void 0 ? void 0 : props.cancelHandler,
            regionCode: props === null || props === void 0 ? void 0 : props.regionCode,
            errorHandler: props === null || props === void 0 ? void 0 : props.errorHandler
          });
        case 3:
          return /*#__PURE__*/React.createElement(RegisterDocumentDetails, {
            ref: childRef,
            consentSection: ((_consentObj = consentObj) === null || _consentObj === void 0 ? void 0 : _consentObj.length) > 0 ? componentObject[0].consentSection : undefined,
            displayFields: (_componentObject$2 = componentObject[0]) === null || _componentObject$2 === void 0 ? void 0 : _componentObject$2.displaySections,
            role: props === null || props === void 0 ? void 0 : props.role,
            regionCode: props === null || props === void 0 ? void 0 : props.regionCode,
            tenantId: tenantId,
            axiosInstance: axiosInstance,
            environment: environment,
            dcsJwtToken: dcsJWTToken,
            clientToken: props === null || props === void 0 ? void 0 : props.clientToken,
            requestorPayload: requestorPayload,
            setOpenRegisterModal: setOpenRegisterModal,
            countryCode: userData === null || userData === void 0 || (_userData$user8 = userData.user) === null || _userData$user8 === void 0 ? void 0 : _userData$user8.countryCode,
            handleActiveTab: handleActiveTab,
            setLoader: function setLoader(value) {
              return _setLoader(value);
            },
            setSnackbar: function setSnackbar(value) {
              return _setSnackbar(value);
            },
            setSnackBarMessage: function setSnackBarMessage(message) {
              return _setSnackBarMessage(message);
            },
            setSnackBarSeverity: function setSnackBarSeverity(severity) {
              return _setSnackBarSeverity(severity);
            },
            successHandler: props === null || props === void 0 ? void 0 : props.successHandler,
            errorHandler: props === null || props === void 0 ? void 0 : props.errorHandler,
            booleanFunc: getBooleanValue
          });
        case 4:
          return /*#__PURE__*/React.createElement(SuccessPage, {
            setOpenRegisterModal: setOpenRegisterModal,
            cancelHandler: props === null || props === void 0 ? void 0 : props.cancelHandler
          });
        default:
          break;
      }
    } else if (props !== null && props !== void 0 && props.dynamicForm) {
      var _consentObj2, _componentObject$3, _componentObject$4, _componentObject$5;
      return /*#__PURE__*/React.createElement(CustomOnboardingForm, {
        ref: childRef,
        consentSection: ((_consentObj2 = consentObj) === null || _consentObj2 === void 0 ? void 0 : _consentObj2.length) > 0 ? (_componentObject$3 = componentObject[0]) === null || _componentObject$3 === void 0 ? void 0 : _componentObject$3.consentSection : undefined,
        stepContent: componentObject[0].stepContent,
        displayFields: (_componentObject$4 = componentObject[0]) === null || _componentObject$4 === void 0 ? void 0 : _componentObject$4.displaySections,
        dynamicForm: props === null || props === void 0 ? void 0 : props.dynamicForm,
        booleanFunc: getBooleanValue,
        order: (_componentObject$5 = componentObject[0]) === null || _componentObject$5 === void 0 ? void 0 : _componentObject$5.order,
        axiosInstance: axiosInstance,
        handleActiveTab: handleActiveTab,
        setLoader: function setLoader(value) {
          return _setLoader(value);
        },
        setSnackbar: function setSnackbar(value) {
          return _setSnackbar(value);
        },
        setSnackBarMessage: function setSnackBarMessage(message) {
          return _setSnackBarMessage(message);
        },
        setSnackBarSeverity: function setSnackBarSeverity(severity) {
          return _setSnackBarSeverity(severity);
        },
        setOpenRegisterModal: setOpenRegisterModal,
        cancelHandler: props === null || props === void 0 ? void 0 : props.cancelHandler,
        successHandler: props === null || props === void 0 ? void 0 : props.successHandler,
        errorHandler: props === null || props === void 0 ? void 0 : props.errorHandler
      });
    }
  };
  var userDetailsProviderValue = useMemo(function () {
    return {
      userData: userData,
      setUserDetailsFromInput: setUserDetailsFromInput
    };
  }, [userData, setUserDetailsFromInput]);
  var companyDetailsProviderValue = useMemo(function () {
    return {
      companyData: companyData,
      setCompanyDetailsFromInput: setCompanyDetailsFromInput
    };
  }, [companyData, setCompanyDetailsFromInput]);
  var panDetailsProviderValue = useMemo(function () {
    return {
      panNameValue: panNameValue,
      setPanNameFromInput: setPanNameFromInput
    };
  }, [panNameValue, setPanNameFromInput]);
  var countryDataProviderValue = useMemo(function () {
    return {
      countryData: countryData
    };
  }, [countryData]);
  var compAttributesProviderValue = useMemo(function () {
    return {
      userCompAttributesData: userCompAttributesData
    };
  }, [userCompAttributesData]);
  var consentDetailsProviderValue = useMemo(function () {
    return {
      consentDataV1: consentDataV1,
      setConsentDetailsV1FromInput: setConsentDetailsV1FromInput
    };
  }, [consentDataV1, setConsentDetailsV1FromInput]);
  var businessTypeDataValue = useMemo(function () {
    return {
      businessTypeData: businessTypeData
    };
  }, [businessTypeData]);
  var consentDataValue = useMemo(function () {
    return {
      consentDataV2: consentDataV2,
      setConsentDetailsV2FromInput: setConsentDetailsV2FromInput
    };
  }, [consentDataV2, setConsentDetailsV2FromInput]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(CssBaseline, {
    enableColorScheme: true
  }), /*#__PURE__*/React.createElement(UserDetailsContext.Provider, {
    value: userDetailsProviderValue
  }, /*#__PURE__*/React.createElement(CompanyDetailsContext.Provider, {
    value: companyDetailsProviderValue
  }, /*#__PURE__*/React.createElement(PanFullNameContext.Provider, {
    value: panDetailsProviderValue
  }, /*#__PURE__*/React.createElement(CountryListDetailsContext.Provider, {
    value: countryDataProviderValue
  }, /*#__PURE__*/React.createElement(UserCompanyAttributeDetailsContext.Provider, {
    value: compAttributesProviderValue
  }, /*#__PURE__*/React.createElement(BusinessTypeDetailsContext.Provider, {
    value: businessTypeDataValue
  }, /*#__PURE__*/React.createElement(ConsentDetailsContextV1.Provider, {
    value: consentDetailsProviderValue
  }, /*#__PURE__*/React.createElement(ConsentDetailsContextV2.Provider, {
    value: consentDataValue
  }, /*#__PURE__*/React.createElement(Dialog, {
    open: openRegisterModal,
    onClose: function onClose(event, reason) {
      if (reason !== 'backdropClick') {
        setOpenRegisterModal(false);
        props === null || props === void 0 || props.cancelHandler();
      }
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      backgroundColor: 'secondary.light',
      position: 'fixed !important',
      left: '50%',
      translate: '-50% -50%',
      width: isMediumDevice ? '90%' : '70%',
      border: 0,
      maxHeight: 'fit-content',
      padding: '21px',
      height: isSmallDevice ? '90%' : 'auto',
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 11
  }, /*#__PURE__*/React.createElement(Box, {
    m: 2
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: isSmallDevice ? '1.3rem' : 'x-large',
      fontWeight: 'bold'
    },
    variant: "h4",
    color: "primary"
  }, isEmpty(props === null || props === void 0 ? void 0 : props.dynamicForm) ? i18n.t('REGISTRATION.REGISTER_COMPANY', {
    role: getDisplayRole()
  }) : props === null || props === void 0 || (_props$dynamicForm2 = props.dynamicForm) === null || _props$dynamicForm2 === void 0 || (_props$dynamicForm2 = _props$dynamicForm2.title) === null || _props$dynamicForm2 === void 0 ? void 0 : _props$dynamicForm2.heading))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 1,
    sx: {
      display: 'flex',
      justifyContent: 'flex-end'
    }
  }, /*#__PURE__*/React.createElement(IconButton, {
    sx: {
      fontSize: '3rem'
    },
    disableFocusRipple: true,
    disableTouchRipple: true,
    disableRipple: true,
    onClick: function onClick() {
      props === null || props === void 0 || props.cancelHandler();
      setOpenRegisterModal(false);
    },
    size: "large"
  }, /*#__PURE__*/React.createElement(CloseIcon, {
    fontSize: "large"
  })))), activeTab !== 4 && /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: ' flex',
      justifyContent: 'center'
    },
    mx: 2
  }, /*#__PURE__*/React.createElement(RegisterTab, {
    activeTab: activeTab,
    dynamicForm: dynamicFormDisplay
  })), /*#__PURE__*/React.createElement(Box, {
    ref: outerModalRef,
    pl: 2,
    pr: 2,
    pb: 1,
    sx: {
      scrollbarWidth: 'thin',
      height: getHeight(),
      paddingRight: paddingOnScroll
    }
  }, /*#__PURE__*/React.createElement(Box, {
    ref: modalRef,
    sx: {
      height: '100%',
      backgroundColor: 'secondary.main',
      padding: '0 21px 28px',
      overflow: 'auto'
    }
  }, getComponent(activeTab))), (!isEmpty(props === null || props === void 0 ? void 0 : props.dynamicForm) || activeTab !== 4) && /*#__PURE__*/React.createElement(Box, {
    sx: {
      backgroundColor: 'secondary.light',
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement(NavigationButtons, {
    activeTab: activeTab,
    handleActiveTab: handleActiveTab,
    onButtonClick: handleButtonClick,
    isSubmitDisabled: isSubmitDisabled,
    setOpenConfirmModal: setOpenConfirmModal,
    setOpenRegisterModal: setOpenRegisterModal,
    cancelHandler: props === null || props === void 0 ? void 0 : props.cancelHandler,
    dynamicForm: props === null || props === void 0 ? void 0 : props.dynamicForm
  })), openConfirmModal && /*#__PURE__*/React.createElement(Dialog, {
    open: openConfirmModal,
    maxWidth: "xs",
    sx: {
      '& .MuiDialog-paper': {
        width: '80%',
        maxHeight: 435
      }
    }
  }, /*#__PURE__*/React.createElement(DialogTitle, null, i18n.t('REGISTRATION.DIALOG_HEADING')), /*#__PURE__*/React.createElement(DialogActions, {
    sx: {
      padding: '16px'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    onClick: function onClick() {
      return setOpenConfirmModal(false);
    },
    sx: {
      textTransform: 'capitalize'
    }
  }, i18n.t('REGISTRATION.NO')), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: function onClick() {
      return handleSubmit();
    },
    sx: {
      textTransform: 'capitalize'
    }
  }, i18n.t('REGISTRATION.YES'))))), openRegisterModal && loader && /*#__PURE__*/React.createElement(Backdrop, {
    open: loader,
    invisible: !loader,
    sx: {
      background: (_props$configOptions = props.configOptions) !== null && _props$configOptions !== void 0 && _props$configOptions.circularProgressBackgroundColor ? (_props$configOptions2 = props.configOptions) === null || _props$configOptions2 === void 0 ? void 0 : _props$configOptions2.circularProgressBackgroundColor : 'rgba(0, 0, 0, 0.5)',
      zIndex: (_props$configOptions3 = props.configOptions) !== null && _props$configOptions3 !== void 0 && _props$configOptions3.circularProgressZIndex ? props.configOptions.circularProgressZIndex : 10000
    }
  }, /*#__PURE__*/React.createElement(CircularProgress, {
    size: (_props$configOptions4 = props.configOptions) !== null && _props$configOptions4 !== void 0 && _props$configOptions4.circularProgressThickness ? (_props$configOptions5 = props.configOptions) === null || _props$configOptions5 === void 0 ? void 0 : _props$configOptions5.circularProgressThickness : 40
  })), snackbar && /*#__PURE__*/React.createElement(Snackbar, {
    open: snackbar,
    autoHideDuration: 2000,
    onClose: handleSnackbar,
    message: snackBarMessage,
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'center'
    }
  }, /*#__PURE__*/React.createElement(Alert, {
    severity: snackBarSeverity,
    action: action
  }, snackBarMessage))), !openRegisterModal && loader && /*#__PURE__*/React.createElement(Backdrop, {
    open: loader,
    invisible: !loader,
    sx: {
      background: (_props$configOptions6 = props.configOptions) !== null && _props$configOptions6 !== void 0 && _props$configOptions6.circularProgressBackgroundColor ? (_props$configOptions7 = props.configOptions) === null || _props$configOptions7 === void 0 ? void 0 : _props$configOptions7.circularProgressBackgroundColor : 'rgba(0, 0, 0, 0.5)',
      zIndex: (_props$configOptions8 = props.configOptions) !== null && _props$configOptions8 !== void 0 && _props$configOptions8.circularProgressZIndex ? props.configOptions.circularProgressZIndex : 10000
    }
  }, /*#__PURE__*/React.createElement(CircularProgress, {
    size: (_props$configOptions9 = props.configOptions) !== null && _props$configOptions9 !== void 0 && _props$configOptions9.circularProgressThickness ? (_props$configOptions10 = props.configOptions) === null || _props$configOptions10 === void 0 ? void 0 : _props$configOptions10.circularProgressThickness : 40
  }))))))))))));
};
export { RegisterCompany };